import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getReferenceImages,
  generateReferenceImage,
  getReferenceImageUrl,
  ReferenceImagesInfo,
} from "../services/mediaService";
import { ScriptResponse } from "../types";

const SMALL_BTN =
  "px-3 py-1 rounded-md text-xs transition whitespace-nowrap disabled:opacity-50";

// Character reference prompts (free to copy) and generated pictures (PRO and Agency)
const ReferenceImagesPanel = ({ script }: { script: ScriptResponse }) => {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [info, setInfo] = useState<ReferenceImagesInfo | null>(null);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [generating, setGenerating] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState("");

  // Load the characters the first time the section is opened
  useEffect(() => {
    if (!open || loaded) return;
    const load = async () => {
      try {
        const data = await getReferenceImages(script.id);
        setInfo(data);
        const urls: Record<string, string> = {};
        for (const c of data.characters) {
          if (c.hasImage) {
            urls[c.characterName] = await getReferenceImageUrl(
              script.id,
              c.characterName,
            );
          }
        }
        setImageUrls(urls);
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            "Could not load the characters. Please try again.",
        );
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, [open, loaded, script.id]);

  // Free the pictures from memory when leaving the page
  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach((u) => window.URL.revokeObjectURL(u));
    };
  }, [imageUrls]);

  const handleCopy = (name: string, prompt: string) => {
    navigator.clipboard.writeText(prompt);
    setCopied(name);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleGenerate = async (name: string) => {
    setError("");
    setGenerating(name);
    try {
      await generateReferenceImage(script.id, name);
      const url = await getReferenceImageUrl(script.id, name);
      setImageUrls((prev) => {
        if (prev[name]) window.URL.revokeObjectURL(prev[name]);
        return { ...prev, [name]: url };
      });
      // Refresh the weekly usage numbers
      const data = await getReferenceImages(script.id);
      setInfo(data);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Could not create the picture. Please try again.",
      );
    } finally {
      setGenerating(null);
    }
  };

  const handleDownload = (name: string) => {
    const url = imageUrls[name];
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = `${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-reference.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const limitReached =
    !!info && info.canGenerate && info.usedThisWeek >= info.weeklyLimit;
  const unlimited = !!info && info.weeklyLimit > 1000;

  return (
    <div className="bg-gray-900 rounded-xl p-4 border border-gray-700">
      {/* Header: click to open or close */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div>
          <h3 className="text-gray-400 text-xs font-semibold uppercase">
            Character reference images
          </h3>
          <p className="text-gray-500 text-xs mt-1">
            One picture per character, so faces stay the same in every shot.
          </p>
        </div>
        <span className="text-gray-400 text-sm">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {!loaded ? (
            <p className="text-gray-400 text-sm">Loading characters...</p>
          ) : (
            <>
              {/* Weekly usage */}
              {info && info.canGenerate && (
                <p className="text-gray-400 text-xs">
                  {unlimited
                    ? "Unlimited images (admin)."
                    : `${info.usedThisWeek} of ${info.weeklyLimit} images used this week. Resets on ${new Date(info.resetsOn).toLocaleDateString()}.`}
                </p>
              )}

              {/* Free plan: copying is free, generating needs PRO or Agency */}
              {info && !info.canGenerate && (
                <div className="bg-gray-800 border border-yellow-600 rounded-lg p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <p className="text-yellow-400 text-xs">
                    Copy the prompts for free, or upgrade to PRO or Agency to
                    generate the pictures here.
                  </p>
                  <button
                    onClick={() => navigate("/pricing")}
                    className={`${SMALL_BTN} bg-yellow-500 hover:bg-yellow-400 text-black font-semibold`}
                  >
                    Upgrade
                  </button>
                </div>
              )}

              {error && (
                <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {info && info.characters.length === 0 && (
                <p className="text-gray-400 text-sm">
                  No characters were found in this script.
                </p>
              )}

              {info?.characters.map((c) => (
                <div
                  key={c.characterName}
                  className="bg-gray-800 rounded-lg p-4 border border-gray-700"
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-purple-400 font-semibold text-sm">
                      {c.characterName}
                      {c.copiedFromPart && (
                        <span className="text-green-400 font-normal text-xs ml-2">
                          From Part {c.copiedFromPart}
                        </span>
                      )}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCopy(c.characterName, c.prompt)}
                        className={`${SMALL_BTN} bg-gray-700 hover:bg-gray-600 text-white`}
                      >
                        {copied === c.characterName ? "Copied!" : "Copy prompt"}
                      </button>
                      {info.canGenerate && (
                        <button
                          onClick={() => handleGenerate(c.characterName)}
                          disabled={generating !== null || limitReached}
                          className={`${SMALL_BTN} bg-purple-600 hover:bg-purple-700 text-white`}
                        >
                          {generating === c.characterName
                            ? "Creating..."
                            : c.copiedFromPart
                              ? "Update outfit"
                              : imageUrls[c.characterName]
                                ? "Regenerate"
                                : "Generate image"}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    {imageUrls[c.characterName] && (
                      <div className="flex-shrink-0">
                        <img
                          src={imageUrls[c.characterName]}
                          alt={`${c.characterName} reference`}
                          className="w-32 h-32 object-cover rounded-lg border border-gray-600"
                        />
                        <button
                          onClick={() => handleDownload(c.characterName)}
                          className={`${SMALL_BTN} bg-gray-700 hover:bg-gray-600 text-white mt-2 w-full`}
                        >
                          Download
                        </button>
                      </div>
                    )}
                    <p className="text-gray-300 text-xs leading-relaxed">
                      {c.prompt}
                    </p>
                  </div>
                </div>
              ))}

              {limitReached && (
                <p className="text-yellow-400 text-xs">
                  You've reached this week's image limit. You can still copy the
                  prompts and make the pictures yourself.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ReferenceImagesPanel;
